import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Inbox, Bell, ShieldCheck, User, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import ProfileEditor from "@/components/ProfileEditor";
import ClaimNumberDialog from "@/components/ClaimNumberDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

function CTable({ rows }) {
  const navigate = useNavigate();
  if (!rows.length) return <p className="text-sm text-slate-500 py-6">No complaints here yet.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="text-left p-3">Complaint ID</th><th className="text-left p-3">Party</th>
            <th className="text-left p-3">Status</th><th className="text-left p-3">Response</th><th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-slate-100" data-testid={`complaint-row-${c.id}`}>
              <td className="p-3 font-mono text-xs">{c.complaint_code}</td>
              <td className="p-3">{c.party?.name}<br /><span className="text-xs text-slate-400">{c.party?.category}</span></td>
              <td className="p-3"><StatusBadge status={c.status} /></td>
              <td className="p-3 text-xs">{c.response ? "Received" : "Pending"}</td>
              <td className="p-3"><Button size="sm" variant="outline" onClick={() => navigate(`/complaint/${c.id}`)}>View</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mine, setMine] = useState([]);
  const [received, setReceived] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [myNumber, setMyNumber] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);

  const loadNumber = () => api.get("/number/mine").then((r) => setMyNumber(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/complaints/mine").then((r) => setMine(r.data)).catch(() => {});
    api.get("/complaints/received").then((r) => setReceived(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    loadNumber();
  }, []);

  const readNotif = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      setNotifs((p) => p.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    if (n.link) navigate(n.link);
  };

  return (
    <div className="ca-container py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">My Dashboard</h1>
          <p className="text-slate-500 text-sm flex items-center gap-1.5 mt-1"><User className="h-4 w-4" /> {user?.name} · {user?.email} · {user?.user_type}</p>
        </div>
        <Link to="/raise-dispute"><Button className="bg-teal-brand hover:bg-teal-700 text-white"><FileText className="h-4 w-4 mr-2" /> Raise a dispute</Button></Link>
      </div>

      <div className="mt-6 grid sm:grid-cols-4 gap-3">
        {[["My complaints", mine.length, FileText], ["Complaints received", received.length, Inbox],
          ["Resolved", mine.filter((c) => c.status === "resolved_by_agreement").length, ShieldCheck],
          ["Unread alerts", notifs.filter((n) => !n.read).length, Bell]].map(([l, v, Icon]) => (
          <div key={l} className="rounded-2xl border border-slate-200 bg-white p-4">
            <Icon className="h-5 w-5 text-teal" />
            <p className="text-2xl font-display font-bold mt-2">{v}</p>
            <p className="text-xs text-slate-500">{l}</p>
          </div>
        ))}
      </div>

      <div className="mt-6"><ProfileEditor /></div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="my-number-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-teal" /> Your verified mobile number
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {myNumber?.phone_verified
                ? <>Verified: <span className="font-mono">{myNumber.masked}</span> — a verified badge shows on your number's public profile.</>
                : "Verify a mobile number to claim your Dispute Transparency Profile and add a public response."}
            </p>
            {myNumber?.public_statement && (
              <p className="text-xs text-slate-600 mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2">“{myNumber.public_statement}”</p>
            )}
          </div>
          <Button variant={myNumber?.phone_verified ? "outline" : "default"}
            className={myNumber?.phone_verified ? "" : "bg-teal-brand hover:bg-teal-700 text-white"}
            onClick={() => setClaimOpen(true)} data-testid="dashboard-claim-number-btn">
            <BadgeCheck className="h-4 w-4 mr-2" /> {myNumber?.phone_verified ? "Update number / response" : "Claim & verify number"}
          </Button>
        </div>
      </div>
      <ClaimNumberDialog open={claimOpen} onOpenChange={setClaimOpen}
        initialPhone={myNumber?.phone || ""} onDone={loadNumber} />

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="mine">
            <TabsList>
              <TabsTrigger value="mine" data-testid="tab-my-complaints">My Complaints</TabsTrigger>
              <TabsTrigger value="received" data-testid="tab-received">Complaints Received</TabsTrigger>
            </TabsList>
            <TabsContent value="mine" className="mt-4"><CTable rows={mine} /></TabsContent>
            <TabsContent value="received" className="mt-4"><CTable rows={received} /></TabsContent>
          </Tabs>
        </div>
        <aside>
          <h3 className="font-display font-bold text-slate-900 mb-3 flex items-center gap-2"><Bell className="h-5 w-5 text-teal" /> Notifications</h3>
          <div className="space-y-2">
            {notifs.length === 0 && <p className="text-sm text-slate-500">No notifications.</p>}
            {notifs.map((n) => (
              <button key={n.id} onClick={() => readNotif(n)} className={`w-full text-left rounded-xl border p-3 ${n.read ? "border-slate-200 bg-white" : "border-teal-brand/30 bg-teal-brand/5"}`}>
                <p className="text-sm font-medium text-slate-800">{n.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
